import { useEffect, useState } from "react";
import styles from "./StartMining.module.scss";
import cn from "classnames";
import { StartMiningProps } from "./StartMining.props";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { useDispatch, useSelector } from "react-redux";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { useIntl } from "react-intl";
import { RootState } from "@store";
import deleteIcon from "@assets/redesign/icons/trash.svg";
import {
  noMinersSelected,
  selectedMinersString,
} from "@store/minersSelect/actionTypes";
import useApiRequests from "@services/http/useApiRequest";
import {
  MinerControlRequest,
  MinerControlType,
  MinerReset,
} from "@services/minerContol";
import { makeNotifs } from "@utils/makeNotifs";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import SmallLink from "@components/global/new/SmallLink/SmallLink";
import { RedoOutlined } from "@ant-design/icons";
import Icon from "@components/global/new/Icon/Icon";
import { useToasts } from "@utils/hooks/useToasts";

import origStart from "@assets/redesign/miningIcons/start.svg";
import stop from "@assets/redesign/miningIcons/stop.svg";
import origReset from "@assets/redesign/miningIcons/reset.svg";
import boostOn from "@assets/redesign/miningIcons/lightning.svg";
import boostOff from "@assets/redesign/miningIcons/lightningGray.svg";
import origCheck from "@assets/redesign/miningIcons/libraryAddCheckFill.svg";
import {
  ALLOW_BOOST_MINER,
  ALLOW_CHECK_MINER,
  WLP202603,
} from "../../../../config";
import { dev } from "@utils/dev";
import wlpStart from "@components/WLP202603/assets/redesign/miningIcons/start.svg";
import wlpReset from "@components/WLP202603/assets/redesign/miningIcons/reset.svg";
import wlpCheck from "@components/WLP202603/assets/redesign/miningIcons/libraryAddCheckFill.svg";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { deleteMinerErrors } from "@services/minerErrors";
import { useMinerContext } from "../MinerRow/MinerContext";
import { MinerActions } from "../MinerRow/minerReducer";

const CONTROL_COMMANDS: ("start" | "stop" | "restart")[] = [
  "start",
  "stop",
  "restart",
];

const start = WLP202603 ? wlpStart : origStart;
const reset = WLP202603 ? wlpReset : origReset;
const check = WLP202603 ? wlpCheck : origCheck;

export default function StartMining({
  status,
  className,
  forAllMiners,
  minerID,
  minerName,
  children,

  onChangeStatus,
  ...props
}: StartMiningProps) {
  const { ok, errors } = makeNotifs();
  const { formatMessage } = useIntl();
  const { onSuccess, onLoader, onError } = useGlobalNotifs();
  const dispatch = useDispatch();
  const requests = useApiRequests();
  const selectedMiners = useSelector((state: RootState) => state.minersSelect);

  const [s, setS] = useState<MINER_STATUSES_E | undefined>(status);

  useEffect(() => {
    if (status !== undefined) {
      setS(status);
    }
  }, [status]);

  const isGreen =
    s !== MINER_STATUSES_E.FAIL &&
    s !== MINER_STATUSES_E.OFF &&
    s !== MINER_STATUSES_E.PAUSED &&
    s !== MINER_STATUSES_E.STOPPED;

  const isRed =
    s === MINER_STATUSES_E.FAIL ||
    s === MINER_STATUSES_E.OFF ||
    // s === StatusE.PAUSED ||
    s === MINER_STATUSES_E.STOPPED;

  const onClickMining = (stat: MINER_STATUSES_E) => {

    if (!minerID && noMinersSelected(selectedMiners))
      return onError(errors.miner_settings__miners);

    let subtitle = "";
    if (stat === MINER_STATUSES_E.STARTING) {
      if (forAllMiners) {
        subtitle = formatMessage({ id: "modal.start_miming" });
      } else {
        subtitle = formatMessage(
          { id: "modal.start_miming.one" },
          { miner: minerName }
        );
      }
    } else if (stat === MINER_STATUSES_E.STOPPED) {
      if (forAllMiners) {
        subtitle = formatMessage({ id: "modal.stop_mining" });
      } else {
        subtitle = formatMessage(
          { id: "modal.stop_mining.one" },
          { miner: minerName }
        );
      }
    }

    let ctrl = CONTROL_COMMANDS[0];

    switch (stat) {
      case MINER_STATUSES_E.STOPPED:
        ctrl = CONTROL_COMMANDS[1];
        break;
      case MINER_STATUSES_E.STARTING:
        ctrl = CONTROL_COMMANDS[0];
        break;
    }

    dispatch(
      show(MODALS_ENUM.CONFIRM_MODAL, {
        title: " ",
        subtitle,
        onConfirm: async () => {
          const req = minerID
            ? new MinerControlRequest(ctrl, minerID.toString())
            : new MinerControlRequest(
              ctrl,
              // remainingMiners.map((r) => r.id).join(",")
              selectedMinersString(selectedMiners)
            );

          const response = await requests.enqueue(req, {
            onLoadingStatusChanged: onLoader,
          });
          if (!response) return;
          if (stat === MINER_STATUSES_E.STOPPED) {
            onChangeStatus?.(MINER_STATUSES_E.STOPPING);
          } else {
            onChangeStatus?.(stat);
          }
          onSuccess(ok.success);
          setS(stat);
        },
      })
    );
  };

  const onReset = async () => {
    if (!minerID && noMinersSelected(selectedMiners))
      return onError(errors.miner_settings__miners);

    const res = await requests.enqueue(
      new MinerReset(
        minerID ? minerID.toString() : selectedMinersString(selectedMiners)
      ),
      {
        onLoadingStatusChanged: onLoader,
      }
    );
    if (!res) return;

    onChangeStatus && onChangeStatus(MINER_STATUSES_E.OFF);
    onSuccess(ok.success);
    setS(MINER_STATUSES_E.OFF);
  };

  const onRestart = async () => {
    if (!minerID && noMinersSelected(selectedMiners))
      return onError(errors.miner_settings__miners);

    const res = await requests.enqueue(
      new MinerControlRequest(
        CONTROL_COMMANDS[2],
        minerID
          ? minerID.toString()
          : selectedMinersString(selectedMiners)
      ),
      {
        onLoadingStatusChanged: onLoader,
      }
    );
    if (!res) return;

    onChangeStatus && onChangeStatus(MINER_STATUSES_E.OFF);
    onSuccess(ok.success);
    setS(MINER_STATUSES_E.OFF);
  };

  const { dispatchMiner } = useMinerContext();
  const { request: clearMinerErrors } = useTypedRequest(deleteMinerErrors);

  const doClearErrors = async () => {
    if (!minerID) return;
    onLoader(true);
    try {
      const response = await clearMinerErrors(minerID);
      if (response && response.Status === "success") {
        onSuccess(ok.success);
        dispatchMiner({
          type: MinerActions.ResetHasErrors,
          payload: {},
        });
      }
    }
    finally {
      onLoader(false);
    }
  };

  const onClickToControls = async (ctrl: MinerControlType) => {

    if (!minerID && noMinersSelected(selectedMiners) && ctrl !== "check")
      return onError(errors.miner_settings__miners);

    const notSelectedMiners = !minerID && !noMinersSelected(selectedMiners);
    const isCheckAll = !notSelectedMiners && ctrl === "check";

    if (ctrl === "check" && minerID) {
      const response = await requests.enqueue(
        new MinerControlRequest(ctrl, minerID.toString()),
        { onLoadingStatusChanged: onLoader }
      );
      if (!response) return;
      onSuccess(ok.success);
      return;
    }

    let subtitle = formatMessage(
      { id: `modal.${ctrl}_mining${ctrl === "check" ? ".one" : ""}` },
      { miner: minerName }
    );

    if (notSelectedMiners) {
      subtitle = formatMessage({ id: `modal.${ctrl}_mining.all` });
    }

    if (isCheckAll && !minerID) {
      subtitle = formatMessage({ id: `modal.check_mining.all.all` });
    }

    dispatch(
      show(MODALS_ENUM.CONFIRM_MODAL, {
        title: " ",
        subtitle,
        onConfirm: async () => {
          const req = minerID
            ? new MinerControlRequest(ctrl, minerID.toString())
            : new MinerControlRequest(
              isCheckAll ? "checkall" : ctrl,
              selectedMinersString(selectedMiners)
            );

          const response = await requests.enqueue(req, {
            onLoadingStatusChanged: onLoader,
          });
          if (!response) return;
          onSuccess(ok.success);
        },
      })
    );
  };

  return (
    <div className={cn(styles.wrapper, className)} {...props}>
      <div className={cn(styles.buttons_container, {})}>
        <label className={styles.title}>
          {formatMessage({ id: "machines.start_stop" })}
        </label>
        <Icon
          className={styles.icon}
          src={start}
          onClick={() => onClickMining(MINER_STATUSES_E.STARTING)}
          title={formatMessage({ id: "machines.start_stop.start" })}
        />
        <Icon
          className={styles.icon}
          src={stop}
          onClick={() => onClickMining(MINER_STATUSES_E.STOPPED)}
          title={formatMessage({ id: "machines.start_stop.stop" })}
        />
        <Icon
          className={styles.icon}
          src={reset}
          onClick={onRestart}
          title={formatMessage({ id: "machines.start_stop.restart" })}
        />
        <SmallLink className={styles.reset} onClick={onReset}>
          {formatMessage({ id: "machines.start_stop.reset" })}
        </SmallLink>
        {ALLOW_BOOST_MINER && (
          <>
            <Icon
              className={styles.icon}
              src={boostOn}
              noHover
              onClick={() => onClickToControls("booston")}
              title={formatMessage({ id: "machines.start_stop.booston.title" })}
            />
            <Icon
              className={styles.icon}
              src={boostOff}
              noHover
              onClick={() => onClickToControls("boostoff")}
              title={formatMessage({
                id: "machines.start_stop.boostoff.title",
              })}
            />
          </>
        )}
        {ALLOW_CHECK_MINER && (
          <Icon
            className={styles.icon}
            src={check}
            noHover
            onClick={() => onClickToControls("check")}
            title={formatMessage({ id: "machines.start_stop.check.title" })}
          />
        )}
        <SmallLink className={styles.reset} onClick={doClearErrors}>
          {formatMessage({ id: "machines.start_stop.clear_errors" })}
        </SmallLink>
        {/* // <Icon
        //   className={styles.icon}
        //   src={deleteIcon}
        //   noHover
        //   onClick={doClearErrors}
        //   title={formatMessage({ id: "machines.start_stop.clear_errors" })}
        // /> */}
        {children}
      </div>
    </div>
  );
}
